import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Minus, Upload, Search, Filter, Download, ScanLine } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import CoverThumb from "@/components/CoverThumb";
import DocumentsDrawer from "@/components/DocumentsDrawer";

export default function InventoryPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get("filter") || "all"; // all | low | out | unique
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [storeFilter, setStoreFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [stockFilter, setStockFilter] = useState(urlFilter === "low" || urlFilter === "out" ? urlFilter : "all");
  const [search, setSearch] = useState("");
  const [openAdjust, setOpenAdjust] = useState(false);
  const [adjustMode, setAdjustMode] = useState("add");
  const [scanOpen, setScanOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);

  // adjust flow: pick category -> pick product -> qty/reason
  const [adjustStep, setAdjustStep] = useState("category"); // category | product | details
  const [selCat, setSelCat] = useState(null);
  const [adjustSearch, setAdjustSearch] = useState("");
  const [formStore, setFormStore] = useState("");
  const [formProduct, setFormProduct] = useState("");
  const [formQty, setFormQty] = useState("");
  const [formAction, setFormAction] = useState("received");
  const [formNote, setFormNote] = useState("");
  const [loading, setLoading] = useState(true);

  const reload = async ({ showSkeleton = false } = {}) => {
    const sid = user.role !== "admin" ? user.store_id : (storeFilter !== "all" ? storeFilter : undefined);
    if (showSkeleton) { setLoading(true); setItems([]); }
    try {
      const { data } = await api.get("/inventory", { params: sid ? { store_id: sid } : {} });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const [s, p, c] = await Promise.all([
        api.get("/stores"), api.get("/products"), api.get("/categories")
      ]);
      setStores(s.data); setProducts(p.data); setCategories(c.data);
      if (user.role !== "admin") setFormStore(user.store_id);
    })();
  }, [user]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload({ showSkeleton: items.length > 0 }); }, [storeFilter]);

  const filtered = useMemo(() => {
    let list = items.filter((i) => {
      if (catFilter !== "all" && i.product?.category_id !== catFilter) return false;
      if (stockFilter === "low" && !i.low_stock) return false;
      if (stockFilter === "out" && i.quantity !== 0) return false;
      if (search) {
        const s = search.toLowerCase();
        const p = i.product || {};
        return (p.name||"").toLowerCase().includes(s) || (p.sku||"").toLowerCase().includes(s) || (p.isbn||"").toLowerCase().includes(s) || (p.author||"").toLowerCase().includes(s);
      }
      return true;
    });
    if (urlFilter === "unique") {
      const seen = new Set();
      list = list.filter(i => {
        const k = i.product?.id;
        if (!k || seen.has(k)) return false;
        seen.add(k); return true;
      });
    }
    return list;
  }, [items, catFilter, stockFilter, search, urlFilter]);

  const submitAdjust = async (e) => {
    e.preventDefault();
    const qty = parseInt(formQty, 10);
    if (!qty || qty <= 0) return toast.error("Enter a positive quantity");
    const change = adjustMode === "add" ? qty : -qty;
    try {
      await api.post("/inventory/adjust", {
        store_id: formStore, product_id: formProduct,
        quantity_change: change, action_type: formAction, note: formNote,
      });
      toast.success(adjustMode === "add" ? `Added ${qty}` : `Subtracted ${qty}`);
      // Keep the dialog open and go back to the category picker so the user
      // can adjust multiple titles without reopening the dialog each time.
      setFormQty(""); setFormNote(""); setFormProduct(""); setSelCat(null);
      setAdjustSearch(""); setAdjustStep("category");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const downloadCsv = async () => {
    const sid = user.role !== "admin" ? user.store_id : (storeFilter !== "all" ? storeFilter : undefined);
    const { data } = await api.get("/reports/inventory", { params: sid ? { store_id: sid } : {}, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = "inventory.csv"; a.click();
  };

  const downloadPdf = async () => {
    const sid = user.role !== "admin" ? user.store_id : (storeFilter !== "all" ? storeFilter : undefined);
    const { data } = await api.get("/reports/inventory.pdf", { params: sid ? { store_id: sid } : {}, responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = "inventory.pdf"; a.click();
  };

  // Barcode-scan → find product by ISBN or SKU → open Adjust dialog on details step
  const handleScan = async (code) => {
    setScanOpen(false);
    try {
      const { data: prod } = await api.get("/products/lookup", { params: { isbn: code } });
      const cat = categories.find(c => c.id === prod.category_id) || null;
      setSelCat(cat);
      setFormProduct(prod.id);
      setAdjustStep("details");
      setAdjustMode("add");
      setFormAction("received");
      if (user.role !== "admin") setFormStore(user.store_id);
      setOpenAdjust(true);
      toast.success(`Scanned: ${prod.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `No book found for code ${code}`);
    }
  };

  const renderDetailsStep = () => {
    const targetStore = user.role === "admin" ? formStore : user.store_id;
    const invMatches = items.filter(i => i.product_id === formProduct && (targetStore ? i.store_id === targetStore : true));
    const currentQty = invMatches.reduce((s, i) => s + (i.quantity || 0), 0);
    const qtyLabel = targetStore
      ? (stores.find(s => s.id === targetStore)?.name || "this store")
      : "all stores";
    const selectedProduct = products.find(p => p.id === formProduct);
    return (
      <form onSubmit={submitAdjust} className="space-y-4 mt-4" data-testid="adjust-step-details">
        <div className="rounded-sm bg-secondary/50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Selected</div>
              <div className="font-medium truncate">{selectedProduct?.name}</div>
              <div className="text-xs text-muted-foreground font-mono truncate">{selectedProduct?.sku} · {selCat?.name}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">On hand</div>
              <div className="font-display text-2xl leading-none tabular-nums" data-testid="adjust-current-qty">{currentQty}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5 truncate max-w-[140px]" title={qtyLabel}>{qtyLabel}</div>
            </div>
          </div>
        </div>
        {user.role === "admin" && (
          <div className="space-y-1.5">
            <Label>Store</Label>
            <Select value={formStore} onValueChange={setFormStore}>
              <SelectTrigger data-testid="adjust-store-select"><SelectValue placeholder="Pick store"/></SelectTrigger>
              <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Quantity</Label>
            <Input type="number" min="1" value={formQty} onChange={(e) => setFormQty(e.target.value)} data-testid="adjust-qty-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Select value={formAction} onValueChange={setFormAction}>
              <SelectTrigger data-testid="adjust-reason-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                {adjustMode === "add" ? (
                  <>
                    <SelectItem value="received">Received shipment</SelectItem>
                    <SelectItem value="transfer_in">Transferred in</SelectItem>
                    <SelectItem value="adjustment">Adjustment (+)</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                    <SelectItem value="transfer_out">Transferred out</SelectItem>
                    <SelectItem value="adjustment">Adjustment (-)</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Note (optional)</Label>
          <Textarea value={formNote} onChange={(e) => setFormNote(e.target.value)} rows={2} data-testid="adjust-note-input"/>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => setOpenAdjust(false)} data-testid="adjust-done-btn">Done</Button>
          <Button type="submit" data-testid="adjust-submit-btn">
            {adjustMode === "add" ? "Add & next" : "Subtract & next"}
          </Button>
        </DialogFooter>
      </form>
    );
  };



  return (
    <div className="space-y-8" data-testid="inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Catalog</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Inventory</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={()=>setScanOpen(true)} data-testid="scan-btn">
            <ScanLine className="w-4 h-4 mr-2" strokeWidth={1.5}/> Scan
          </Button>
          <Button variant="outline" onClick={()=>setDocsOpen(true)} data-testid="upload-btn">
            <Upload className="w-4 h-4 mr-2" strokeWidth={1.5}/> Upload
          </Button>
          <Dialog open={openAdjust} onOpenChange={(o)=>{ setOpenAdjust(o); if(!o){ setAdjustStep("category"); setSelCat(null); setFormProduct(""); setFormQty(""); setFormNote(""); setAdjustSearch(""); }}}>
            <DialogTrigger asChild>
              <Button data-testid="adjust-inventory-btn"><Plus className="w-4 h-4 mr-2" strokeWidth={1.5}/> Adjust</Button>
            </DialogTrigger>
            <DialogContent className="w-[calc(100vw-1rem)] max-w-lg max-h-[85vh] flex flex-col overflow-hidden p-4 sm:p-6 gap-3">
              <DialogHeader className="shrink-0">
                <DialogTitle className="font-display flex items-center gap-2">
                  {adjustStep !== "category" && (
                    <button type="button" onClick={()=>{
                      setAdjustSearch("");
                      if (adjustStep === "details") setAdjustStep("product");
                      else if (adjustStep === "product") { setAdjustStep("category"); setSelCat(null); }
                    }} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="adjust-back-btn">← Back</button>
                  )}
                  Adjust inventory
                </DialogTitle>
                <DialogDescription className="sr-only">Add or subtract stock for a product across the network.</DialogDescription>
              </DialogHeader>
              <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
              <Tabs value={adjustMode} onValueChange={(v) => { setAdjustMode(v); setFormAction(v==="add"?"received":"sale"); }}>
                <TabsList className="grid grid-cols-2">
                  <TabsTrigger value="add" data-testid="adjust-add-tab">Add stock</TabsTrigger>
                  <TabsTrigger value="subtract" data-testid="adjust-subtract-tab">Subtract</TabsTrigger>
                </TabsList>

                {/* STEP 1: pick a category (or search across all titles) */}
                {adjustStep === "category" && (
                  <div className="mt-4 space-y-3" data-testid="adjust-step-category">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5}/>
                      <Input
                        value={adjustSearch}
                        onChange={(e)=>setAdjustSearch(e.target.value)}
                        placeholder="Search title, SKU, author, ISBN…"
                        className="pl-9"
                        autoFocus
                        data-testid="adjust-search-input"
                      />
                    </div>
                    {adjustSearch.trim() ? (
                      (() => {
                        const q = adjustSearch.trim().toLowerCase();
                        const matches = products.filter(p =>
                          (p.name||"").toLowerCase().includes(q) ||
                          (p.sku||"").toLowerCase().includes(q) ||
                          (p.author||"").toLowerCase().includes(q) ||
                          (p.isbn||"").toLowerCase().includes(q)
                        );
                        const catMap = Object.fromEntries(categories.map(c => [c.id, c]));
                        return (
                          <>
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">
                              {matches.length} match{matches.length === 1 ? "" : "es"}
                            </div>
                            <div className="flex flex-col gap-1.5 max-h-[340px] overflow-y-auto pr-1">
                              {matches.map(p => (
                                <button key={p.id} type="button"
                                  onClick={()=>{ setSelCat(catMap[p.category_id] || null); setFormProduct(p.id); setAdjustStep("details"); }}
                                  className="flex w-full min-w-0 items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-secondary/60 transition-colors text-left"
                                  data-testid={`adjust-search-prod-${p.sku}`}>
                                  <div className="min-w-0">
                                    <div className="font-medium truncate">{p.name}</div>
                                    <div className="text-xs text-muted-foreground font-mono truncate">{p.sku}{catMap[p.category_id] ? ` · ${catMap[p.category_id].name}` : ""}{p.author ? ` · ${p.author}` : ""}</div>
                                  </div>
                                  <span className="text-muted-foreground shrink-0 ml-3">→</span>
                                </button>
                              ))}
                              {matches.length === 0 && <div className="text-center py-8 text-sm text-muted-foreground">No titles match</div>}
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      <>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">Or choose a category</div>
                        <div className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto pr-1">
                          {categories.map(c => {
                            const count = products.filter(p => p.category_id === c.id).length;
                            return (
                              <button key={c.id} type="button"
                                onClick={()=>{ setSelCat(c); setAdjustStep("product"); }}
                                className="flex w-full min-w-0 items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-secondary/60 transition-colors text-left"
                                data-testid={`adjust-cat-${c.code}`}>
                                <div>
                                  <div className="font-medium">{c.name}</div>
                                  <div className="text-xs text-muted-foreground">{count} title{count===1?"":"s"}</div>
                                </div>
                                <span className="text-muted-foreground">→</span>
                              </button>
                            );
                          })}
                          {categories.length === 0 && <div className="text-center py-8 text-sm text-muted-foreground">No categories yet</div>}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* STEP 2: pick a book from that category */}
                {adjustStep === "product" && selCat && (
                  <div className="mt-4 space-y-3" data-testid="adjust-step-product">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5}/>
                      <Input
                        value={adjustSearch}
                        onChange={(e)=>setAdjustSearch(e.target.value)}
                        placeholder={`Search within ${selCat.name}…`}
                        className="pl-9"
                        autoFocus
                        data-testid="adjust-search-input"
                      />
                    </div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Titles in {selCat.name}</div>
                    <div className="flex flex-col gap-1.5 max-h-[320px] overflow-y-auto pr-1">
                      {(() => {
                        const q = adjustSearch.trim().toLowerCase();
                        const list = products.filter(p => p.category_id === selCat.id && (
                          !q || (p.name||"").toLowerCase().includes(q) || (p.sku||"").toLowerCase().includes(q) || (p.author||"").toLowerCase().includes(q) || (p.isbn||"").toLowerCase().includes(q)
                        ));
                        return (
                          <>
                            {list.map(p => (
                              <button key={p.id} type="button"
                                onClick={()=>{ setFormProduct(p.id); setAdjustStep("details"); }}
                                className="flex w-full min-w-0 items-center justify-between px-4 py-3 border border-border rounded-sm hover:bg-secondary/60 transition-colors text-left"
                                data-testid={`adjust-prod-${p.sku}`}>
                                <div className="min-w-0">
                                  <div className="font-medium truncate">{p.name}</div>
                                  <div className="text-xs text-muted-foreground font-mono truncate">{p.sku}{p.author ? ` · ${p.author}` : ""}</div>
                                </div>
                                <span className="text-muted-foreground shrink-0 ml-3">→</span>
                              </button>
                            ))}
                            {list.length === 0 && (
                              <div className="text-center py-8 text-sm text-muted-foreground">
                                {q ? "No titles match your search" : "No titles in this category yet"}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* STEP 3: qty + reason */}
                {adjustStep === "details" && renderDetailsStep()}
              </Tabs>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid-border-card p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input className="pl-9" placeholder="Search by name, SKU, ISBN, author…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="inventory-search"/>
        </div>
        {user.role === "admin" && (
          <Select value={storeFilter} onValueChange={setStoreFilter}>
            <SelectTrigger className="w-[200px]" data-testid="filter-store"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stores</SelectItem>
              {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[180px]" data-testid="filter-category"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger className="w-[160px]" data-testid="filter-stock"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stock</SelectItem>
            <SelectItem value="low">Low stock</SelectItem>
            <SelectItem value="out">Out of stock</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid-border-card overflow-hidden">
        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-border">
          {loading && items.length === 0 && Array.from({ length: 6 }).map((_, i) => (
            <div key={`sk-${i}`} className="p-4 flex gap-3 animate-pulse" data-testid="inventory-row-skeleton">
              <div className="h-12 w-12 bg-muted rounded-sm shrink-0"/>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="h-3 w-3/4 bg-muted rounded-sm"/>
                    <div className="h-3 w-1/2 bg-muted rounded-sm"/>
                  </div>
                  <div className="h-4 w-8 bg-muted rounded-sm"/>
                </div>
                <div className="h-3 w-2/3 bg-muted rounded-sm"/>
                <div className="h-5 w-20 bg-muted rounded-full"/>
              </div>
            </div>
          ))}
          {filtered.map((i) => (
            <div key={i.id} className="p-4 flex gap-3" data-testid="inventory-row">
              <CoverThumb coverPath={i.product?.cover_path} size={48}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{i.product?.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{i.product?.author}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-base leading-tight">{i.quantity}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Qty</div>
                  </div>
                </div>
                <div className="mt-2 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {i.product?.sku && (<><span className="font-mono">{i.product.sku}</span><span>·</span></>)}
                  <span className="truncate">{i.category_name}</span>
                  {user.role === "admin" && (<><span>·</span><span className="truncate">{i.store_name}</span></>)}
                </div>
                <div className="mt-2">
                  {i.quantity === 0
                    ? <Badge variant="destructive">Out of stock</Badge>
                    : i.low_stock
                    ? <Badge className="bg-accent/15 text-accent hover:bg-accent/20">Low · min {i.product?.min_stock_level}</Badge>
                    : <Badge variant="secondary">In stock</Badge>}
                </div>
              </div>
            </div>
          ))}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-muted-foreground text-sm">No items match your filters</div>
          )}
        </div>
        {/* Tablet+ table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead className="bg-secondary/60 text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3 w-14"></th>
                <th className="text-left px-4 py-3">SKU</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Author</th>
                <th className="text-left px-4 py-3">Category</th>
                {user.role === "admin" && <th className="text-left px-4 py-3">Store</th>}
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-right px-4 py-3">Min</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading && items.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk-${i}`} className="animate-pulse" data-testid="inventory-row-skeleton-table">
                  <td className="px-4 py-3"><div className="h-9 w-9 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-16 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-56 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-32 bg-muted rounded-sm"/></td>
                  <td className="px-4 py-3"><div className="h-3 w-24 bg-muted rounded-sm"/></td>
                  {user.role === "admin" && <td className="px-4 py-3"><div className="h-3 w-24 bg-muted rounded-sm"/></td>}
                  <td className="px-4 py-3 text-right"><div className="h-3 w-6 bg-muted rounded-sm ml-auto"/></td>
                  <td className="px-4 py-3 text-right"><div className="h-3 w-6 bg-muted rounded-sm ml-auto"/></td>
                  <td className="px-4 py-3"><div className="h-5 w-16 bg-muted rounded-full"/></td>
                </tr>
              ))}
              {filtered.map((i) => (
                <tr key={i.id} className="hover:bg-secondary/40 transition-colors" data-testid="inventory-row">
                  <td className="px-4 py-3"><CoverThumb coverPath={i.product?.cover_path} size={36}/></td>
                  <td className="px-4 py-3 font-mono text-xs">{i.product?.sku || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3 font-medium">{i.product?.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{i.product?.author}</td>
                  <td className="px-4 py-3 text-muted-foreground">{i.category_name}</td>
                  {user.role === "admin" && <td className="px-4 py-3 text-muted-foreground">{i.store_name}</td>}
                  <td className="px-4 py-3 text-right font-mono">{i.quantity}</td>
                  <td className="px-4 py-3 text-right font-mono text-muted-foreground">{i.product?.min_stock_level}</td>
                  <td className="px-4 py-3">
                    {i.quantity === 0
                      ? <Badge variant="destructive">Out</Badge>
                      : i.low_stock
                      ? <Badge className="bg-accent/15 text-accent hover:bg-accent/20">Low</Badge>
                      : <Badge variant="secondary">In stock</Badge>}
                  </td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={user.role==="admin"?9:8} className="text-center py-12 text-muted-foreground">No items match your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BarcodeScanner open={scanOpen} onClose={()=>setScanOpen(false)} onDetected={handleScan}/>
      <DocumentsDrawer open={docsOpen} onOpenChange={setDocsOpen} stores={stores} onSuccess={reload}/>
    </div>
  );
}
